import { Dispatch, SetStateAction } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { NavLink } from 'react-router-dom';

import { numberCap } from 'helpers';
import { MenuNavItem } from 'types';

interface Props {
  showItems: boolean;
  setSelectedItem: Dispatch<SetStateAction<string | null>>;
  itemProps: MenuNavItem;
}

export function NavItemButton({
  showItems,
  setSelectedItem,
  itemProps,
}: Props) {
  const { icon, title, itemList } = itemProps;

  const generalNotification = itemList.some(({ notification }) => notification);
  const generalNumericalIndicator = itemList.reduce(
    (acc, { numericalIndicator }) => {
      if (numericalIndicator && numericalIndicator > acc)
        acc = acc + numericalIndicator;

      return acc;
    },
    0
  );

  const hasNotification =
    generalNotification || Boolean(generalNumericalIndicator);

  const hasActiveChild = itemList.some(
    ({ path }) => window.location.pathname === path
  );
  const shouldShowParentSelection = hasActiveChild && !showItems;
  const hasSingleChild = itemList.length === 1;
  const singleChildPath = hasSingleChild ? itemList[0].path : null;

  const getMenuItemClassName = (isActive: boolean) =>
    `border-solid border-l-[6px] ${
      isActive
        ? 'bg-neutral-500 border-primary-300'
        : 'bg-neutral-700 hover:bg-neutral-500 hover:border-neutral-500 border-neutral-700'
    }`;

  const renderNotification = () => (
    <span
      className="min-w-6 h-6 rounded-full bg-primary-300 font-bold text-xs flex items-center
        justify-center"
    >
      {Boolean(generalNumericalIndicator) ? (
        numberCap(generalNumericalIndicator, 99)
      ) : (
        <b className="px-2.5 font-bold">Novo!</b>
      )}
    </span>
  );

  const renderIconAndTitle = () => (
    <div className="flex items-center">
      <div className="flex items-center justify-center size-16">{icon}</div>
      <span className="text-base">{title}</span>
    </div>
  );

  if (hasSingleChild) {
    return (
      <NavLink
        to={singleChildPath!}
        className={({ isActive }) =>
          `flex items-center justify-between text-neutral-100 cursor-pointer relative
          ${getMenuItemClassName(isActive)}`
        }
      >
        {renderIconAndTitle()}
        {hasNotification && (
          <div className="flex items-center justify-center size-16">
            {renderNotification()}
          </div>
        )}
      </NavLink>
    );
  }

  const activeMenuItemClassName = getMenuItemClassName(
    showItems || shouldShowParentSelection ? shouldShowParentSelection : false
  );

  return (
    <div className="flex flex-col">
      <button
        onClick={() => {
          showItems ? setSelectedItem(null) : setSelectedItem(title);
        }}
        className={`flex items-center justify-between text-neutral-100 cursor-pointer relative
          ${activeMenuItemClassName}`}
      >
        {renderIconAndTitle()}

        <div className="flex items-center">
          {hasNotification && renderNotification()}

          <div className="flex items-center justify-center size-16">
            {showItems ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </div>
      </button>

      {showItems && (
        <div className="flex flex-col bg-neutral-700">
          {itemList.map(({ name, path, notification, numericalIndicator }) => {
            const hasItemNotification =
              notification || Boolean(numericalIndicator);

            return (
              <NavLink
                to={path}
                className={({ isActive }) =>
                  [
                    `flex items-center justify-between text-sm text-neutral-100 p-3 cursor-pointer
                    no-underline relative ${getMenuItemClassName(isActive)}`,
                  ].join('')
                }
                key={name}
              >
                {name}

                {hasItemNotification && (
                  <span
                    className="min-w-6 h-6 rounded-full bg-primary-300 font-bold text-xs flex items-center
                      justify-center"
                  >
                    {Boolean(numericalIndicator) ? (
                      numberCap(numericalIndicator || 0, 99)
                    ) : (
                      <b className="px-2.5 font-bold">Novo!</b>
                    )}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}
